import {
  AskStrikeUpButton,
  Badge,
  Box,
  Button,
  Card,
  Flex,
  Heading,
  HStack,
  Input,
  PreHeader,
  Stack,
  Text,
  VStack,
} from "components/ui";
import { SetupGuideLink } from "components/common/SetupGuideLink";
import { useTranslation } from "hooks/useTranslation";
import { useEffect, useRef, useState } from "react";
import {
  MdAdd,
  MdCheck,
  MdClose,
  MdDelete,
  MdEdit,
  MdLink,
  MdRefresh,
} from "react-icons/md";
import {
  integrationService,
  SupportIntegration,
} from "services/integrationService";
import IntercomTokenForm from "./IntercomTokenForm";

const STATUS_DOT: Record<string, string> = {
  Active: "var(--accent-success, #22C55E)",
  Disconnected: "var(--text-tertiary-color, #88898E)",
  Error: "var(--accent-danger, #EF4444)",
};

const PROVIDER_LABEL: Record<string, string> = {
  HelpScout: "Help Scout",
  Intercom: "Intercom",
};

const Integrations = () => {
  const { t } = useTranslation();
  const [integrations, setIntegrations] = useState<SupportIntegration[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [connectingHelpScout, setConnectingHelpScout] = useState(false);
  const [showIntercomWebhookTip, setShowIntercomWebhookTip] = useState(false);
  const [showTokenForm, setShowTokenForm] = useState(false);
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState("");
  const [renameSaving, setRenameSaving] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const oauthWindowRef = useRef<Window | null>(null);

  useEffect(() => {
    loadIntegrations();

    const handleMessage = (e: MessageEvent) => {
      if (e.data === "handoff_connected") {
        setConnectingHelpScout(false);
        loadIntegrations();
      }
    };
    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, []);

  // Poll for OAuth popup close (Help Scout)
  useEffect(() => {
    if (!connectingHelpScout) return;
    const interval = setInterval(() => {
      if (oauthWindowRef.current?.closed) {
        setConnectingHelpScout(false);
        loadIntegrations();
        clearInterval(interval);
      }
    }, 1000);
    return () => clearInterval(interval);
  }, [connectingHelpScout]);

  const loadIntegrations = async () => {
    try {
      setLoading(true);
      setError("");
      const data = await integrationService.listIntegrations();
      setIntegrations(data);
    } catch (err: any) {
      setError(err.response?.data?.error || "Failed to load integrations.");
    } finally {
      setLoading(false);
    }
  };

  const handleConnectHelpScout = async (integrationId?: string) => {
    try {
      setConnectingHelpScout(true);
      const { authorizationUrl } =
        await integrationService.beginHelpScoutOAuth(integrationId);
      const popup = window.open(
        authorizationUrl,
        "helpscout_oauth",
        "width=600,height=700,noopener",
      );
      oauthWindowRef.current = popup;
      if (!popup) {
        window.location.href = authorizationUrl;
      }
    } catch (err: any) {
      setError(
        err.response?.data?.error ||
          "Failed to start Help Scout authorization.",
      );
      setConnectingHelpScout(false);
    }
  };

  const handleIntercomConnected = () => {
    setShowTokenForm(false);
    loadIntegrations();
    setShowIntercomWebhookTip(true);
  };

  const handleStartRename = (integration: SupportIntegration) => {
    setRenamingId(integration.id);
    setRenameValue(integration.name);
  };

  const handleRename = async (id: string) => {
    if (!renameValue.trim()) return;
    try {
      setRenameSaving(true);
      await integrationService.renameIntegration(id, renameValue.trim());
      setIntegrations((prev) =>
        prev.map((i) => (i.id === id ? { ...i, name: renameValue.trim() } : i)),
      );
      setRenamingId(null);
    } catch (err: any) {
      setError(err.response?.data?.error || "Failed to rename integration.");
    } finally {
      setRenameSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (
      !window.confirm(
        "Disconnect this integration? This will remove it from any widgets using it.",
      )
    )
      return;
    try {
      setDeletingId(id);
      await integrationService.deleteIntegration(id);
      setIntegrations((prev) => prev.filter((i) => i.id !== id));
    } catch (err: any) {
      setError(
        err.response?.data?.error || "Failed to disconnect integration.",
      );
    } finally {
      setDeletingId(null);
    }
  };

  const getReconnectHandler = (integration: SupportIntegration) =>
    integration.providerType === "Intercom"
      ? () => setShowTokenForm(true)
      : () => handleConnectHelpScout(integration.id);

  const webhookUrl = `${process.env.REACT_APP_BASE_API_URL}/api/webhooks/intercom`;

  return (
    <Stack gap={5} w="full">
      <Flex
        justifyContent="space-between"
        alignItems="center"
        flexWrap="wrap"
        gap={3}
      >
        <VStack align="start" gap={1}>
          <Heading size="lg" color="var(--text-primary)">
            Integrations
          </Heading>
          <Text fontSize="sm" color="var(--text-secondary-color)" m={0}>
            Connect support platforms to enable human handoff from any widget.
          </Text>
        </VStack>
        <HStack gap={2}>
          <AskStrikeUpButton
            context={t("app.askStrikeUp.context.integrations")}
          />
          <SetupGuideLink doc="support-integrations-setup" />
          <Button
            intent="primary"
            onClick={() => handleConnectHelpScout()}
            disabled={connectingHelpScout}
          >
            <MdAdd />
            {connectingHelpScout ? "Connecting…" : "Connect Help Scout"}
          </Button>
          <Button
            intent="secondary"
            onClick={() => setShowTokenForm((v) => !v)}
          >
            <MdAdd />
            Connect Intercom
          </Button>
        </HStack>
      </Flex>

      {showTokenForm && (
        <IntercomTokenForm
          onConnected={handleIntercomConnected}
          onClose={() => setShowTokenForm(false)}
        />
      )}

      {/* Intercom webhook setup instructions (shown automatically after OAuth) */}
      {showIntercomWebhookTip && (
        <Box
          p={4}
          borderRadius="10px"
          borderWidth="1px"
          borderColor="var(--brand-primary, #3B82F6)"
          bg="rgba(59, 130, 246, 0.06)"
        >
          <Flex justifyContent="space-between" alignItems="flex-start" gap={3}>
            <Box flex={1}>
              <Box mb={2}>
                <PreHeader size="xs">Setup required</PreHeader>
              </Box>
              <Text fontWeight={500} mb={2} color="var(--text-primary)" m={0}>
                Intercom Connected — Manual Webhook Setup Required
              </Text>
              <Text
                fontSize="sm"
                mb={3}
                color="var(--text-secondary-color)"
                m={0}
              >
                Intercom does not support automatic webhook registration. To
                receive agent replies in your widget, open your{" "}
                <Text as="span" fontWeight={500} color="var(--text-primary)">
                  Intercom Developer Hub → Webhooks
                </Text>{" "}
                and add the following:
              </Text>
              <Stack gap={2} fontSize="sm">
                <Box>
                  <Text
                    fontFamily="var(--font-mono)"
                    fontSize="10px"
                    letterSpacing="0.10em"
                    textTransform="uppercase"
                    color="var(--text-secondary-color)"
                    m={0}
                  >
                    Webhook URL
                  </Text>
                  <Box
                    as="code"
                    display="block"
                    mt={1}
                    px={3}
                    py={2}
                    borderRadius="6px"
                    bg="var(--bg-secondary)"
                    fontSize="xs"
                    fontFamily="var(--font-mono)"
                    wordBreak="break-all"
                    color="var(--text-primary)"
                  >
                    {webhookUrl}
                  </Box>
                </Box>
                <Box>
                  <Text
                    fontFamily="var(--font-mono)"
                    fontSize="10px"
                    letterSpacing="0.10em"
                    textTransform="uppercase"
                    color="var(--text-secondary-color)"
                    m={0}
                  >
                    Subscribe to topics
                  </Text>
                  <HStack gap={2} mt={1} flexWrap="wrap">
                    {[
                      "conversation.admin.replied",
                      "conversation.admin.assigned",
                      "conversation.admin.closed",
                    ].map((t) => (
                      <Box
                        key={t}
                        as="code"
                        px={2}
                        py={0.5}
                        borderRadius="4px"
                        bg="var(--bg-secondary)"
                        fontSize="xs"
                        fontFamily="var(--font-mono)"
                        color="var(--text-primary)"
                      >
                        {t}
                      </Box>
                    ))}
                  </HStack>
                </Box>
              </Stack>
              <Box mt={3}>
                <SetupGuideLink
                  doc="support-integrations-setup"
                  section="intercom"
                  intent="ghost"
                />
              </Box>
            </Box>
            <Button
              size="sm"
              intent="ghost"
              onClick={() => setShowIntercomWebhookTip(false)}
              flexShrink={0}
            >
              <MdClose />
            </Button>
          </Flex>
        </Box>
      )}

      {error && (
        <Box
          p={3}
          borderRadius="10px"
          bg="rgba(239, 68, 68, 0.08)"
          borderWidth="1px"
          borderColor="var(--accent-danger, #EF4444)"
        >
          <Text fontSize="sm" color="var(--text-primary)" m={0}>
            {error}
          </Text>
        </Box>
      )}

      {loading ? (
        <Text color="var(--text-secondary-color)" m={0}>
          Loading integrations…
        </Text>
      ) : integrations.length === 0 ? (
        <Card.Root>
          <Card.Body>
            <VStack gap={4} py={8} textAlign="center">
              <Box fontSize="3xl" color="var(--text-tertiary-color, #88898E)">
                <MdLink />
              </Box>
              <Heading size="md" color="var(--text-primary)" m={0}>
                No integrations yet
              </Heading>
              <Text color="var(--text-secondary-color)" maxW="sm" m={0}>
                Connect a support platform to enable human handoff for your
                widgets.
              </Text>
              <HStack gap={2}>
                <Button
                  intent="primary"
                  onClick={() => handleConnectHelpScout()}
                  disabled={connectingHelpScout}
                >
                  <MdAdd />
                  {connectingHelpScout ? "Connecting…" : "Connect Help Scout"}
                </Button>
                <Button
                  intent="secondary"
                  onClick={() => setShowTokenForm(true)}
                >
                  <MdAdd />
                  Connect Intercom
                </Button>
              </HStack>
            </VStack>
          </Card.Body>
        </Card.Root>
      ) : (
        <Stack gap={2.5}>
          {integrations.map((integration) => (
            <Box
              key={integration.id}
              borderWidth="1px"
              borderColor="var(--border-default)"
              borderRadius="10px"
              bg="var(--bg-primary)"
              p={4}
              transition="background-color 0.15s, border-color 0.15s"
            >
              <Flex justifyContent="space-between" alignItems="center" gap={4}>
                <HStack gap={3} flex={1} minW={0}>
                  <Box
                    w={10}
                    h={10}
                    borderRadius="8px"
                    bg="var(--bg-secondary)"
                    borderWidth="1px"
                    borderColor="var(--border-default)"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    flexShrink={0}
                    color="var(--text-secondary-color)"
                  >
                    <MdLink size={20} />
                  </Box>
                  <Box minW={0}>
                    {renamingId === integration.id ? (
                      <HStack gap={2}>
                        <Input
                          size="sm"
                          value={renameValue}
                          onChange={(e) => setRenameValue(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") handleRename(integration.id);
                            if (e.key === "Escape") setRenamingId(null);
                          }}
                          autoFocus
                          w="200px"
                        />
                        <Button
                          size="sm"
                          intent="primary"
                          onClick={() => handleRename(integration.id)}
                          disabled={renameSaving}
                        >
                          <MdCheck />
                        </Button>
                        <Button
                          size="sm"
                          intent="ghost"
                          onClick={() => setRenamingId(null)}
                        >
                          Cancel
                        </Button>
                      </HStack>
                    ) : (
                      <Text
                        fontWeight={500}
                        color="var(--text-primary)"
                        truncate
                        m={0}
                      >
                        {integration.name}
                      </Text>
                    )}
                    <HStack gap={2} mt={1} align="center">
                      <HStack gap={1.5} align="center">
                        <Box
                          w="6px"
                          h="6px"
                          borderRadius="full"
                          bg={
                            STATUS_DOT[integration.status] ??
                            "var(--text-tertiary-color, #88898E)"
                          }
                          flexShrink={0}
                        />
                        <Text
                          fontFamily="var(--font-mono)"
                          fontSize="10px"
                          letterSpacing="0.10em"
                          textTransform="uppercase"
                          color="var(--text-secondary-color)"
                          m={0}
                        >
                          {integration.status}
                        </Text>
                      </HStack>
                      <Badge colorPalette="blue" size="sm" variant="subtle">
                        {PROVIDER_LABEL[integration.providerType] ??
                          integration.providerType}
                      </Badge>
                    </HStack>
                  </Box>
                </HStack>

                <HStack gap={2} flexShrink={0}>
                  <Button
                    size="sm"
                    intent="ghost"
                    onClick={() => handleStartRename(integration)}
                    disabled={renamingId === integration.id}
                  >
                    <MdEdit />
                    Rename
                  </Button>
                  {integration.status !== "Active" && (
                    <Button
                      size="sm"
                      intent="ghost"
                      onClick={getReconnectHandler(integration)}
                      disabled={connectingHelpScout}
                    >
                      <MdRefresh />
                      Reconnect
                    </Button>
                  )}
                  <Button
                    size="sm"
                    intent="danger"
                    onClick={() => handleDelete(integration.id)}
                    disabled={deletingId === integration.id}
                  >
                    <MdDelete />
                    Disconnect
                  </Button>
                </HStack>
              </Flex>
            </Box>
          ))}
        </Stack>
      )}
    </Stack>
  );
};

export default Integrations;
